import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from '@e965/xlsx';
import './ExcelExportModal.css';

interface ExcelExportModalProps {
    projectId: string;
    token: string;
    onClose: () => void;
}

interface Project {
    id: number;
    name: string;
    description: string | null;
    status: string;
    rtdKontoId: number | null;
    rtdKonto?: {
        id: number;
        accountName: string;
        institute: string;
        iban: string;
        bic: string;
        glaeubigerId: string;
    };
}

interface RechnungPosition {
    id: number;
    description: string;
    pricePerItem: number;
    numberOfItems: number;
    tax: number;
}

interface Rechnung {
    id: number;
    amount: number;
    date: string;
    projectId: number;
    pdfPath?: string | null;
    roundTable?: {
        id: number;
        name: string;
        number: number;
        iban: string;
        bic: string;
        kontoInhaber: string;
        bankName: string;
        billingName: string;
        billingEmail: string;
        billingPlz: string;
        billingCity: string;
        billingStreet: string;
        billingStreetNumber: string;
    };
    rechnungPositions?: RechnungPosition[];
}

interface ColumnOption {
    id: string;
    label: string;
    category: 'project' | 'invoice';
}

const ExcelExportModal: React.FC<ExcelExportModalProps> = ({ projectId, token, onClose }) => {
    const [project, setProject] = useState<Project | null>(null);
    const [rechnungen, setRechnungen] = useState<Rechnung[]>([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());

    // Available columns
    const availableColumns: ColumnOption[] = [
        // Project columns
        { id: 'project.id', label: 'Projekt-ID', category: 'project' },
        { id: 'project.name', label: 'Projektname', category: 'project' },
        { id: 'project.description', label: 'Projektbeschreibung', category: 'project' },
        { id: 'project.status', label: 'Projektstatus', category: 'project' },
        { id: 'project.rtdKonto.accountName', label: 'RTD Kontoname', category: 'project' },
        { id: 'project.rtdKonto.institute', label: 'RTD Institut', category: 'project' },
        { id: 'project.rtdKonto.iban', label: 'RTD IBAN', category: 'project' },
        { id: 'project.rtdKonto.bic', label: 'RTD BIC', category: 'project' },
        { id: 'project.rtdKonto.glaeubigerId', label: 'RTD Gläubiger-ID', category: 'project' },
        
        // Invoice columns
        { id: 'invoice.id', label: 'Rechnungs-ID', category: 'invoice' },
        { id: 'invoice.date', label: 'Rechnungsdatum', category: 'invoice' },
        { id: 'invoice.amount', label: 'Rechnungsbetrag', category: 'invoice' },
        { id: 'invoice.roundTable.number', label: 'Round Table Nummer', category: 'invoice' },
        { id: 'invoice.roundTable.name', label: 'Round Table Name', category: 'invoice' },
        { id: 'invoice.roundTable.iban', label: 'Round Table IBAN', category: 'invoice' },
        { id: 'invoice.roundTable.bic', label: 'Round Table BIC', category: 'invoice' },
        { id: 'invoice.roundTable.kontoInhaber', label: 'Round Table Kontoinhaber', category: 'invoice' },
        { id: 'invoice.roundTable.bankName', label: 'Round Table Bankname', category: 'invoice' },
        { id: 'invoice.roundTable.billingName', label: 'Round Table Rechnungsname', category: 'invoice' },
        { id: 'invoice.roundTable.billingEmail', label: 'Round Table Rechnungs-Email', category: 'invoice' },
        { id: 'invoice.roundTable.billingPlz', label: 'Round Table PLZ', category: 'invoice' },
        { id: 'invoice.roundTable.billingCity', label: 'Round Table Stadt', category: 'invoice' },
        { id: 'invoice.roundTable.billingStreet', label: 'Round Table Straße', category: 'invoice' },
        { id: 'invoice.roundTable.billingStreetNumber', label: 'Round Table Hausnummer', category: 'invoice' },
        { id: 'invoice.positions', label: 'Positionen', category: 'invoice' },
        { id: 'invoice.hasPdf', label: 'PDF vorhanden', category: 'invoice' },
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch project
                const projectResponse = await fetch(`/api/projects/${projectId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!projectResponse.ok) {
                    throw new Error('Fehler beim Laden des Projekts');
                }

                const projectData: Project = await projectResponse.json();
                setProject(projectData);

                // Fetch rechnungen
                const rechnungenResponse = await fetch(`/api/rechnungen/project/${projectId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!rechnungenResponse.ok) {
                    throw new Error('Fehler beim Laden der Rechnungen');
                }

                const rechnungenData: Rechnung[] = await rechnungenResponse.json();
                setRechnungen(rechnungenData);

                // Pre-select some common columns
                setSelectedColumns(new Set([
                    'project.name',
                    'invoice.roundTable.name',
                    'invoice.roundTable.number',
                    'invoice.date',
                    'invoice.amount',
                    'invoice.positions'
                ]));
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Daten');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [projectId, token]);

    const handleColumnToggle = (columnId: string) => {
        const newSelected = new Set(selectedColumns);
        if (newSelected.has(columnId)) {
            newSelected.delete(columnId);
        } else {
            newSelected.add(columnId);
        }
        setSelectedColumns(newSelected);
    };

    const handleSelectAll = (category?: 'project' | 'invoice') => {
        const columnsToSelect = category
            ? availableColumns.filter(col => col.category === category)
            : availableColumns;
        
        setSelectedColumns(new Set(columnsToSelect.map(col => col.id)));
    };

    const handleDeselectAll = () => {
        setSelectedColumns(new Set());
    };

    const formatCurrency = (amount: number): string => {
        // Amount is expected in cents, convert to euros
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(amount / 100);
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    const getStatusLabel = (status: string): string => {
        switch (status) {
            case 'OPEN':
                return 'Offen';
            case 'CLOSED':
                return 'Geschlossen';
            case 'ARCHIVED':
                return 'Archiviert';
            default:
                return status;
        }
    };

    const getCellValue = (columnId: string, rechnung?: Rechnung): string => {
        if (!project) return '';

        // Project columns
        if (columnId === 'project.id') return project.id.toString();
        if (columnId === 'project.name') return project.name;
        if (columnId === 'project.description') return project.description || '';
        if (columnId === 'project.status') return getStatusLabel(project.status);
        if (columnId === 'project.rtdKonto.accountName') return project.rtdKonto?.accountName || '';
        if (columnId === 'project.rtdKonto.institute') return project.rtdKonto?.institute || '';
        if (columnId === 'project.rtdKonto.iban') return project.rtdKonto?.iban || '';
        if (columnId === 'project.rtdKonto.bic') return project.rtdKonto?.bic || '';
        if (columnId === 'project.rtdKonto.glaeubigerId') return project.rtdKonto?.glaeubigerId || '';

        // Invoice columns (require rechnung)
        if (!rechnung) return '';

        if (columnId === 'invoice.id') return rechnung.id.toString();
        if (columnId === 'invoice.date') return formatDate(rechnung.date);
        if (columnId === 'invoice.amount') return formatCurrency(rechnung.amount);
        if (columnId === 'invoice.roundTable.number') return rechnung.roundTable?.number.toString() || '';
        if (columnId === 'invoice.roundTable.name') return rechnung.roundTable?.name || '';
        if (columnId === 'invoice.roundTable.iban') return rechnung.roundTable?.iban || '';
        if (columnId === 'invoice.roundTable.bic') return rechnung.roundTable?.bic || '';
        if (columnId === 'invoice.roundTable.kontoInhaber') return rechnung.roundTable?.kontoInhaber || '';
        if (columnId === 'invoice.roundTable.bankName') return rechnung.roundTable?.bankName || '';
        if (columnId === 'invoice.roundTable.billingName') return rechnung.roundTable?.billingName || '';
        if (columnId === 'invoice.roundTable.billingEmail') return rechnung.roundTable?.billingEmail || '';
        if (columnId === 'invoice.roundTable.billingPlz') return rechnung.roundTable?.billingPlz || '';
        if (columnId === 'invoice.roundTable.billingCity') return rechnung.roundTable?.billingCity || '';
        if (columnId === 'invoice.roundTable.billingStreet') return rechnung.roundTable?.billingStreet || '';
        if (columnId === 'invoice.roundTable.billingStreetNumber') return rechnung.roundTable?.billingStreetNumber || '';
        if (columnId === 'invoice.hasPdf') return rechnung.pdfPath ? 'Ja' : 'Nein';
        
        if (columnId === 'invoice.positions') {
            if (!rechnung.rechnungPositions || rechnung.rechnungPositions.length === 0) {
                return '';
            }
            // Concatenate positions with newline, showing description and total (without tax rate)
            return rechnung.rechnungPositions.map(pos => {
                const totalAmount = pos.pricePerItem * pos.numberOfItems;
                return `${pos.description} (${pos.numberOfItems}x ${formatCurrency(pos.pricePerItem)} = ${formatCurrency(totalAmount)})`;
            }).join('\n');
        }

        return '';
    };

    const handleExport = () => {
        if (selectedColumns.size === 0) {
            toast.warning('Bitte wählen Sie mindestens eine Spalte aus');
            return;
        }

        if (!project) {
            toast.error('Projektdaten nicht verfügbar');
            return;
        }

        setExporting(true);

        try {
            // Create export data - one row per invoice
            const exportData = rechnungen.map(rechnung => {
                const row: Record<string, string> = {};
                
                selectedColumns.forEach(columnId => {
                    const column = availableColumns.find(col => col.id === columnId);
                    if (column) {
                        row[column.label] = getCellValue(columnId, rechnung);
                    }
                });

                return row;
            });

            // If no invoices, create at least one row with project data
            if (exportData.length === 0) {
                const row: Record<string, string> = {};
                selectedColumns.forEach(columnId => {
                    const column = availableColumns.find(col => col.id === columnId);
                    if (column && column.category === 'project') {
                        row[column.label] = getCellValue(columnId);
                    }
                });
                if (Object.keys(row).length > 0) {
                    exportData.push(row);
                }
            }

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Set column widths for better readability
            const colWidths = Array.from(selectedColumns).map(columnId => {
                const column = availableColumns.find(col => col.id === columnId);
                // Special handling for positions column
                if (columnId === 'invoice.positions') {
                    return { wch: 50 };
                }
                // Default widths based on label length
                return { wch: Math.max(15, (column?.label.length || 15) + 5) };
            });
            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Projektexport');

            // Generate filename with project name and current date
            const date = new Date().toISOString().split('T')[0];
            // Remove only characters that are invalid in filenames
            const safeProjectName = project.name.replace(/[<>:"/\\|?*]/g, '_');
            const filename = `${safeProjectName}_Export_${date}.xlsx`;

            // Save file
            XLSX.writeFile(wb, filename);

            toast.success('Excel-Datei wurde erfolgreich heruntergeladen');
            onClose();
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Fehler beim Exportieren der Excel-Datei');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="wizard-overlay" onClick={onClose}>
            <div className="wizard-modal excel-export-modal" onClick={(e) => e.stopPropagation()}>
                <div className="wizard-header">
                    <h2>Excel Export</h2>
                    <button className="wizard-close-btn" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="wizard-content">
                    {loading ? (
                        <div className="export-loading">Lade Daten...</div>
                    ) : (
                        <>
                            <div className="export-info">
                                <p>
                                    <strong>Projekt:</strong> {project?.name}
                                </p>
                                <p>
                                    <strong>Rechnungen:</strong> {rechnungen.length}
                                </p>
                                <p className="export-description">
                                    Wählen Sie die Spalten aus, die in der Excel-Datei enthalten sein sollen.
                                    Die Datei enthält eine Zeile pro Rechnung.
                                </p>
                            </div>

                            <div className="column-selection-section">
                                <div className="column-selection-header">
                                    <h4>Spaltenwahl</h4>
                                    <div className="column-selection-actions">
                                        <button
                                            className="column-action-btn"
                                            onClick={() => handleSelectAll()}
                                        >
                                            Alle auswählen
                                        </button>
                                        <button
                                            className="column-action-btn"
                                            onClick={handleDeselectAll}
                                        >
                                            Alle abwählen
                                        </button>
                                    </div>
                                </div>

                                <div className="columns-by-category">
                                    <div className="column-category">
                                        <div className="category-header">
                                            <h5>Projektfelder</h5>
                                            <button
                                                className="category-select-btn"
                                                onClick={() => handleSelectAll('project')}
                                            >
                                                Alle
                                            </button>
                                        </div>
                                        <div className="column-list">
                                            {availableColumns
                                                .filter(col => col.category === 'project')
                                                .map(column => (
                                                    <label key={column.id} className="column-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedColumns.has(column.id)}
                                                            onChange={() => handleColumnToggle(column.id)}
                                                        />
                                                        <span>{column.label}</span>
                                                    </label>
                                                ))}
                                        </div>
                                    </div>

                                    <div className="column-category">
                                        <div className="category-header">
                                            <h5>Rechnungsfelder</h5>
                                            <button
                                                className="category-select-btn"
                                                onClick={() => handleSelectAll('invoice')}
                                            >
                                                Alle
                                            </button>
                                        </div>
                                        <div className="column-list">
                                            {availableColumns
                                                .filter(col => col.category === 'invoice')
                                                .map(column => (
                                                    <label key={column.id} className="column-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedColumns.has(column.id)}
                                                            onChange={() => handleColumnToggle(column.id)}
                                                        />
                                                        <span>
                                                            {column.label}
                                                            {column.id === 'invoice.positions' && (
                                                                <span className="column-note">
                                                                    {' '}(werden mit Zeilenumbruch getrennt)
                                                                </span>
                                                            )}
                                                        </span>
                                                    </label>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="selected-count">
                                    {selectedColumns.size} Spalte{selectedColumns.size !== 1 ? 'n' : ''} ausgewählt
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="wizard-footer">
                    <button
                        className="cancel-button"
                        onClick={onClose}
                        disabled={exporting}
                    >
                        Abbrechen
                    </button>
                    <button
                        className="export-button"
                        onClick={handleExport}
                        disabled={loading || exporting || selectedColumns.size === 0}
                    >
                        {exporting ? 'Exportiere...' : 'Exportieren'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExcelExportModal;
